import React, { useState, useEffect, useRef } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import { useMediaQuery, useTheme } from "@mui/material";
import SoftButton from "../../components/SoftButton";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftInput from "../../components/SoftInput";

import { GET_IMAGE } from "../../helpers/images";
import { PUT_JOB_IMAGE, PUT_NEW_TAGS } from "../../api/jobs";
import { useDispatch } from "react-redux";
import { getJob } from "../../redux/jobs/actions";
import { UseCompanyColors } from "../../hooks/company";
import { DEFAULT_BRAND, brandRgba, brandDarken } from "../../helpers/brandColor";
import "./feedStyles.css";
import ImageCropper from "./ImageCropper";
import { shareToast } from "./ShareToast";

const DEFAULT_TITLES = ["Before", "After", ""];
export default function FeedGallery({
  jobId,
  images,
  open,
  exit,
  setGallery,
  gallery,
}) {
  const [titles, setTitles] = useState([]);
  const [imageIdx, setImageIdx] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [imageUpdated, setImageUpdated] = useState(false);
  const [tagEdited, setTagEdited] = useState(false);
  const [editingTitleIdx, setEditingTitleIdx] = useState(null);
  // Cropped photos staged locally (index -> File) until Save Changes is
  // pressed — nothing is sent to the server until then.
  const [pendingFiles, setPendingFiles] = useState({});
  const imageInputRef = useRef();
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { primaryColor } = UseCompanyColors();
  const brand = primaryColor || DEFAULT_BRAND;
  const brandDark = brandDarken(brand, 0.18);

  const adddGalleryImage = (imageKey, index) => {
    if (index < gallery.length) {
      const newGallery = [...gallery];
      newGallery[index] = GET_IMAGE(imageKey);
      setGallery(newGallery);
    } else {
      setGallery([...gallery, GET_IMAGE(imageKey)]);
    }
    setImageUpdated(true);
  };

  const addTag = (tag, index) => {
    const newTags = DEFAULT_TITLES.map((item, idx) =>
      titles[idx] ? titles[idx] : item,
    );
    newTags[index] = tag;
    setTitles(newTags);
    setTagEdited(true);
  };

  useEffect(() => {
    if (images && images.length) {
      setGallery(images.map((imgData) => GET_IMAGE(imgData.src)));
      setTitles(images.map((imgData) => imgData.tag));
    }
  }, [images]);

  useEffect(() => {
    return () => {
      setIsUploading(false);
    };
  }, []);

  const handleInputChange = async (e) => {
    if (e.target.files.length === 0) {
      return;
    }

    const imgSrc = e.target.files[0];
    if (imgSrc) {
      setImageSrc(URL.createObjectURL(imgSrc));
      setOpenDialog(true);
    }
  };

  const onSaveCroppedImage = (croppedImageFile) => {
    if (!croppedImageFile) return;

    const targetIdx = imageIdx;
    const localPreviewUrl = URL.createObjectURL(croppedImageFile);

    // Local preview only — nothing is uploaded to the server until the user
    // presses Save Changes. Stage the cropped file for that step.
    if (targetIdx < gallery.length) {
      const previewGallery = [...gallery];
      previewGallery[targetIdx] = localPreviewUrl;
      setGallery(previewGallery);
    } else {
      setGallery([...gallery, localPreviewUrl]);
    }
    setPendingFiles((prev) => ({ ...prev, [targetIdx]: croppedImageFile }));

    setOpenDialog(false);
    imageInputRef.current.value = null;
  };

  const handleUpload = (index) => {
    imageInputRef.current.value = null;
    setImageIdx(index);
    imageInputRef.current.click();
  };

  const handleRemove = (idx) => (e) => {
    e.stopPropagation();
    // Clear this slot in place. Filtering the array would shift every later
    // photo down an index, making it show up under the wrong Before/After
    // slot instead of the one that was actually removed.
    const next = [...gallery];
    next[idx] = undefined;
    setGallery(next);
    setPendingFiles((prev) => {
      if (!(idx in prev)) return prev;
      const nextPending = { ...prev };
      delete nextPending[idx];
      return nextPending;
    });
    setImageUpdated(true);
  };

  // Uploads one staged file to the server, reusing the same isUploading /
  // uploadProgress state the tile overlays already render off of.
  const uploadPendingFile = async (idx, file) => {
    setImageIdx(idx);
    setUploadProgress(0);
    setIsUploading(true);
    const formData = new FormData();
    formData.append("image", file);
    formData.append("index", idx);
    formData.append("tag", titles[idx] || DEFAULT_TITLES[idx]);
    try {
      const { data } = await PUT_JOB_IMAGE(jobId, formData, (progressEvent) => {
        if (!progressEvent.total) return;
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      });
      adddGalleryImage(data.key, JSON.parse(data.index));
      return true;
    } catch (err) {
      console.log("Job image upload error", err.message);
      // Clear this slot in place — see the note in handleRemove above.
      setGallery((prev) => {
        const next = [...prev];
        next[idx] = undefined;
        return next;
      });
      shareToast.error("Upload failed", `Couldn't save the ${titles[idx] || DEFAULT_TITLES[idx] || "photo"} slot. Please try again.`);
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveThenClose = async () => {
    const pendingEntries = Object.entries(pendingFiles);
    let failedUploads = 0;

    if (pendingEntries.length) {
      setIsSaving(true);
      // eslint-disable-next-line no-restricted-syntax
      for (const [idxStr, file] of pendingEntries) {
        const idx = Number(idxStr);
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadPendingFile(idx, file);
        if (!uploaded) failedUploads += 1;
        setPendingFiles((prev) => {
          const next = { ...prev };
          delete next[idx];
          return next;
        });
      }
      setIsSaving(false);
    }

    if (tagEdited) {
      await PUT_NEW_TAGS(jobId, titles);
    }
    if (pendingEntries.length || tagEdited || imageUpdated) {
      dispatch(getJob(jobId));
      if (pendingEntries.length && !failedUploads) {
        shareToast.success("Saved", "Your photos have been uploaded.");
      }
    }
    setTagEdited(false);
    setImageUpdated(false);
    exit();
  };

  // Discard any local-only previews/pending uploads and revert to the last
  // saved state instead of leaving them staged for next time this opens.
  const handleCancel = () => {
    if (isSaving) return;
    if (images && images.length) {
      setGallery(images.map((imgData) => GET_IMAGE(imgData.src)));
      setTitles(images.map((imgData) => imgData.tag));
    } else {
      setGallery([]);
      setTitles([]);
    }
    setPendingFiles({});
    setTagEdited(false);
    setImageUpdated(false);
    exit();
  };

  const uploadedCount = gallery ? gallery.filter(Boolean).length : 0;
  const hasAnyImage = uploadedCount > 0;
  const canSave = hasAnyImage || tagEdited;

  const desktopHeaderContent = (
    <SoftBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        flexShrink: 0,
        padding: "1.5rem 1.75rem",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
      }}
    >
      <SoftBox display="flex" alignItems="center" gap={1.5}>
        <SoftBox
          sx={{
            width: "44px",
            height: "44px",
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            background: `linear-gradient(135deg, ${brand} 0%, ${brandDark} 100%)`,
            boxShadow: `0px 8px 16px ${brandRgba(brand, 0.28)}`,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 16.5L8.5 12a2 2 0 0 1 2.83 0L16 16.5M14 14.5l1.5-1.5a2 2 0 0 1 2.83 0L20 14.5M5 20h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1Z" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="8.5" cy="8.5" r="1.5" fill="#fff" />
          </svg>
        </SoftBox>
        <SoftBox>
          <SoftTypography sx={{ fontSize: "18px!important", fontWeight: "700!important", color: "#0F172A", lineHeight: "22px" }}>
            Upload Job Images
          </SoftTypography>
          <SoftTypography sx={{ fontSize: "13px!important", fontWeight: "500!important", color: "#667085" }}>
            Add before &amp; after photos for this job
          </SoftTypography>
        </SoftBox>
      </SoftBox>
      <SoftButton
        color="white"
        onClick={handleCancel}
        sx={{
          borderRadius: "50%",
          minWidth: "40px !important",
          minHeight: "40px !important",
          width: "40px",
          height: "40px",
          padding: "0",
          background: "rgba(15,23,42,0.04) !important",
          transition: "background 150ms ease",
          "&:hover": { background: "rgba(15,23,42,0.08) !important" },
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: "pointer" }}>
          <path d="M15.9785 6.77246C16.2866 6.46447 16.7866 6.4645 17.0947 6.77246C17.4028 7.08056 17.4027 7.58051 17.0947 7.88867L13.0488 11.9336L17.0938 15.9785C17.4019 16.2867 17.4019 16.7866 17.0938 17.0947C16.7857 17.4027 16.2866 17.4026 15.9785 17.0947L11.9336 13.0498L7.88965 17.0947C7.58152 17.4029 7.08156 17.4029 6.77344 17.0947C6.46537 16.7866 6.46533 16.2866 6.77344 15.9785L10.8174 11.9336L6.77246 7.88867C6.46465 7.5805 6.46445 7.08051 6.77246 6.77246C7.0805 6.4644 7.58052 6.46458 7.88867 6.77246L11.9336 10.8174L15.9785 6.77246Z" fill="#475467" />
        </svg>
      </SoftButton>
    </SoftBox>
  );

  const desktopBodyContent = (
    <SoftBox sx={{ flex: 1, overflowY: "auto", padding: "1.75rem" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: "1.5rem",
        }}
      >
        {DEFAULT_TITLES.map((item, i) => {
          const hasImage = gallery && gallery[i];

          const tileContent = hasImage ? (
            <Box
              key={`image-${item}`}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                "&:hover .gallery-delete-btn": { opacity: 1 },
                "&:hover .gallery-hover-overlay": { opacity: 1 },
              }}
            >
              <img
                src={gallery[i]}
                alt="job"
                onClick={() => handleUpload(i)}
                className="object-cover h-full w-full"
                style={{ cursor: "pointer", display: "block" }}
              />
              <Box
                className="gallery-hover-overlay"
                onClick={() => handleUpload(i)}
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(180deg, rgba(15,23,42,0) 45%, rgba(15,23,42,0.6) 100%)",
                  opacity: 0,
                  transition: "opacity 150ms ease",
                  cursor: "pointer",
                }}
              >
                <SoftBox
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  sx={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                  Click to replace
                </SoftBox>
              </Box>
              <Box
                component="button"
                type="button"
                className="gallery-delete-btn"
                aria-label="Remove image"
                onClick={handleRemove(i)}
                sx={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  background: "rgba(15,23,42,0.55)",
                  backdropFilter: "blur(4px)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.3)",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  opacity: 0,
                  transition: "opacity 150ms ease, background 150ms ease",
                  "&:hover": { background: "#DC2626" },
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </Box>
            </Box>
          ) : (
            <SoftBox sx={{ textAlign: "center", cursor: "pointer", padding: "1rem" }}>
              <SoftBox
                sx={{
                  width: "48px",
                  height: "48px",
                  margin: "0 auto 0.75rem",
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: brandRgba(brand, 0.12),
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={brand} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 16V4M12 4L7 9M12 4l5 5" />
                  <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                </svg>
              </SoftBox>
              <SoftTypography sx={{ fontWeight: 700, fontSize: "14px", lineHeight: "20px", color: "#0F172A" }}>
                Click to upload
              </SoftTypography>
              <SoftTypography sx={{ fontWeight: 500, fontSize: "12px", lineHeight: "18px", color: "#98A2B3" }}>
                PNG or JPG, up to 20MB
              </SoftTypography>
            </SoftBox>
          );

          return (
            <Box key={`img${item}`}>
              <SoftTypography
                sx={{
                  fontStyle: "normal",
                  fontWeight: 600,
                  fontSize: "13px",
                  lineHeight: "18px",
                  color: "#475467",
                  marginBottom: "0.5rem",
                }}
              >
                Image title
              </SoftTypography>
              <SoftInput
                fullWidth
                placeholder="e.g. Before"
                defaultValue={titles[i] ? titles[i] : item}
                onChange={(e) => addTag(e.target.value, i)}
                className="mb-3"
              />
              <Paper
                elevation={0}
                className="w-full overflow-hidden flex items-center justify-center"
                onClick={() => handleUpload(i)}
                sx={{
                  position: "relative",
                  aspectRatio: "1 / 1",
                  background: hasImage ? "#0F172A" : brandRgba(brand, 0.04),
                  border: hasImage
                    ? "1px solid rgba(15,23,42,0.08)"
                    : `1.5px dashed ${brandRgba(brand, 0.35)}`,
                  borderRadius: "14px",
                  cursor: "pointer",
                  transition: "border-color 200ms ease, background 200ms ease, transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    borderColor: brand,
                    background: hasImage ? "#0F172A" : brandRgba(brand, 0.07),
                    transform: "translateY(-2px)",
                    boxShadow: `0px 12px 24px -8px ${brandRgba(brand, 0.25)}`,
                  },
                }}
              >
                {tileContent}
                {i === imageIdx && isUploading && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "14px",
                      background: "rgba(15,23,42,0.55)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    <CircularProgress variant="determinate" value={uploadProgress} size={34} sx={{ color: "#fff" }} />
                    <SoftTypography sx={{ fontSize: "11px!important", fontWeight: "700!important", color: "#fff" }}>
                      {uploadProgress}%
                    </SoftTypography>
                  </Box>
                )}
              </Paper>
            </Box>
          );
        })}
      </Box>

      <input
        ref={imageInputRef}
        onInput={handleInputChange}
        accept="image/*"
        type="file"
        hidden
      />
      <ImageCropper
        imgSrc={imageSrc}
        open={openDialog}
        setOpen={setOpenDialog}
        onSaveCroppedImage={onSaveCroppedImage}
      />
    </SoftBox>
  );

  const mobileHeaderContent = (
    <SoftBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{ flexShrink: 0, padding: "16px 16px 10px" }}
    >
      <SoftBox display="flex" alignItems="center" gap={1}>
        <SoftBox
          sx={{
            width: "26px",
            height: "26px",
            borderRadius: "7px",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            background: `linear-gradient(135deg, ${brand} 0%, ${brandDark} 100%)`,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 16.5L8.5 12a2 2 0 0 1 2.83 0L16 16.5M14 14.5l1.5-1.5a2 2 0 0 1 2.83 0L20 14.5M5 20h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1Z" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="8.5" cy="8.5" r="1.4" fill="#fff" />
          </svg>
        </SoftBox>
        <SoftTypography sx={{ fontSize: "15px!important", fontWeight: "700!important", color: "#0F172A", lineHeight: "18px" }}>
          Upload Job Images
        </SoftTypography>
      </SoftBox>
      <SoftTypography
        sx={{
          fontSize: "11px!important",
          fontWeight: "700!important",
          color: hasAnyImage ? brand : "#94A3B8",
          background: hasAnyImage ? brandRgba(brand, 0.1) : "rgba(148,163,184,0.12)",
          padding: "3px 9px",
          borderRadius: "999px",
        }}
      >
        {uploadedCount}/3
      </SoftTypography>
    </SoftBox>
  );

  // Mobile: one compact card per slot — thumbnail, upload/replace, remove and
  // title all live on the same square instead of a separate label + input +
  // dashed box + trailing button stacked full-width per image.
  const mobileBodyContent = (
    <SoftBox sx={{ flex: 1, overflowY: "auto", padding: "6px 16px 16px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
        {DEFAULT_TITLES.map((item, i) => {
          const hasImage = gallery && gallery[i];
          const displayTitle = titles[i] ? titles[i] : item;

          const showUploadOverlay = i === imageIdx && isUploading;

          let thumbContent;
          if (hasImage) {
            thumbContent = (
              <>
                <img
                  src={gallery[i]}
                  alt="job"
                  className="object-cover h-full w-full"
                  style={{ display: "block" }}
                />
                <Box
                  component="button"
                  type="button"
                  aria-label="Remove image"
                  onClick={handleRemove(i)}
                  sx={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    background: "rgba(15,23,42,0.6)",
                    backdropFilter: "blur(4px)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.35)",
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </Box>
                {/* Title, auto-filled from the slot default (Before/After), shown
                    as a chip on the thumbnail — tap to rename. */}
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingTitleIdx(i);
                  }}
                  sx={{
                    position: "absolute",
                    bottom: "4px",
                    left: "4px",
                    right: "4px",
                    background: "rgba(15,23,42,0.55)",
                    backdropFilter: "blur(3px)",
                    borderRadius: "6px",
                    padding: "2px 5px",
                    cursor: "text",
                  }}
                >
                  {editingTitleIdx === i ? (
                    <input
                      ref={(el) => el && el.focus()}
                      defaultValue={displayTitle}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => addTag(e.target.value, i)}
                      onBlur={() => setEditingTitleIdx(null)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      style={{
                        width: "100%",
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "#fff",
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        textAlign: "center",
                        padding: 0,
                      }}
                    />
                  ) : (
                    <SoftTypography
                      sx={{
                        fontSize: "10px!important",
                        fontWeight: "700!important",
                        color: "#fff",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      }}
                    >
                      {displayTitle || "Add title"}
                    </SoftTypography>
                  )}
                </Box>
              </>
            );
          } else {
            thumbContent = (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={brand} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 7h3l1.5-2h7L17 7h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
                  <circle cx="12" cy="13" r="3.5" />
                </svg>
                <SoftTypography sx={{ fontSize: "10px!important", fontWeight: "600!important", color: brand, textAlign: "center" }}>
                  {item || "Add"}
                </SoftTypography>
              </Box>
            );
          }

          return (
            <Paper
              key={`m-img-${item}`}
              elevation={0}
              onClick={() => handleUpload(i)}
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "1 / 1",
                overflow: "hidden",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "14px",
                background: hasImage ? "#0F172A" : brandRgba(brand, 0.05),
                border: hasImage
                  ? "1px solid rgba(15,23,42,0.08)"
                  : `1.5px dashed ${brandRgba(brand, 0.4)}`,
              }}
            >
              {thumbContent}
              {showUploadOverlay && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "14px",
                    background: "rgba(15,23,42,0.55)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "3px",
                  }}
                >
                  <CircularProgress variant="determinate" value={uploadProgress} size={22} sx={{ color: "#fff" }} />
                  <SoftTypography sx={{ fontSize: "8px!important", fontWeight: "700!important", color: "#fff" }}>
                    {uploadProgress}%
                  </SoftTypography>
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>

      <input
        ref={imageInputRef}
        onInput={handleInputChange}
        accept="image/*"
        type="file"
        hidden
      />
      <ImageCropper
        imgSrc={imageSrc}
        open={openDialog}
        setOpen={setOpenDialog}
        onSaveCroppedImage={onSaveCroppedImage}
      />
    </SoftBox>
  );

  // Same company-brand gradient on both breakpoints — no need for a separate
  // mobile/desktop hue now that this follows the actual brand color.
  const saveGradient = `linear-gradient(95.76deg, ${brand} -1.6%, ${brandDark} 105.5%) !important`;
  const saveShadow = `0px 4px 8px ${brandRgba(brand, 0.22)}`;
  const saveShadowHover = `0px 6px 14px ${brandRgba(brand, 0.3)}`;

  const footerContent = (
    <SoftBox
      display="flex"
      justifyContent={isMobile ? "stretch" : "flex-end"}
      alignItems="center"
      gap={isMobile ? 1.25 : 1.5}
      sx={{
        flexShrink: 0,
        padding: isMobile ? "14px 16px calc(16px + env(safe-area-inset-bottom, 0px))" : "1.25rem 1.75rem",
        borderTop: "1px solid rgba(15,23,42,0.06)",
        borderBottomLeftRadius: "20px",
        borderBottomRightRadius: "20px",
        background: isMobile ? "#fff" : "rgba(255,255,255,0.5)",
      }}
    >
      <SoftButton
        variant="outlined"
        color="secondary"
        onClick={handleCancel}
        disabled={isSaving}
        sx={{
          color: "#475467 !important",
          padding: "0.6rem 1.25rem",
          minWidth: isMobile ? 0 : "120px",
          flex: isMobile ? 1 : "0 0 auto",
          border: "1px solid rgba(15,23,42,0.12)",
          backgroundColor: "transparent",
          borderRadius: "10px",
          "&:hover": {
            backgroundColor: "rgba(15,23,42,0.04)",
          },
        }}
      >
        Cancel
      </SoftButton>
      <SoftButton
        variant="contained"
        onClick={handleSaveThenClose}
        disabled={!canSave || isSaving}
        sx={{
          color: canSave ? "#fff !important" : "#94A3B8 !important",
          padding: "0.6rem 1.25rem",
          minWidth: isMobile ? 0 : "140px",
          flex: isMobile ? 1 : "0 0 auto",
          border: "none",
          borderRadius: "10px",
          background: canSave ? saveGradient : "#E2E5ED !important",
          boxShadow: canSave ? saveShadow : "none",
          cursor: canSave && !isSaving ? "pointer" : "not-allowed",
          "&:hover": {
            background: canSave ? saveGradient : "#E2E5ED !important",
            boxShadow: canSave ? saveShadowHover : "none",
          },
        }}
      >
        {isSaving ? "Saving…" : "Save Changes"}
      </SoftButton>
    </SoftBox>
  );

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleCancel}
        PaperProps={{
          className: "!m-4",
          style: {
            // The paper also has a "!m-4" (16px) margin below, so subtract
            // that here — otherwise width+margin can exceed 100vw and clip
            // on the narrowest phones (e.g. 320px-wide devices).
            width: isMobile ? "calc(100vw - 32px)" : "min(94vw, 720px)",
            maxHeight: "88vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: "20px",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.72) 100%)",
            boxShadow:
              "0px 24px 48px -8px rgba(16,24,40,0.25), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
            border: "1px solid rgba(255,255,255,0.6)",
            overflow: "hidden",
            backdropFilter: "blur(20px) saturate(160%)",
            WebkitBackdropFilter: "blur(20px) saturate(160%)",
          },
        }}
      >
        {isMobile ? mobileHeaderContent : desktopHeaderContent}
        {isMobile ? mobileBodyContent : desktopBodyContent}
        {footerContent}
      </Dialog>
    </div>
  );
}
